const deformationFunctions = /* glsl */ `
uniform sampler2D uChainTexture;
uniform sampler2D uFrameTexture;
uniform float uJointCount;
uniform float uRibbonLength;
uniform float uRibbonWidth;
uniform float uTime;
uniform float uTurbulenceAmplitude;
uniform float uTurbulenceScale;
uniform float uTurbulenceSpeed;

vec3 mod289(vec3 value) {
  return value - floor(value * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 value) {
  return value - floor(value * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 value) {
  return mod289(((value * 34.0) + 10.0) * value);
}

vec4 taylorInvSqrt(vec4 value) {
  return 1.79284291400159 - 0.85373472095314 * value;
}

float simplexNoise(vec3 point) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(point + dot(point, C.yyy));
  vec3 x0 = point - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(
    permute(
      permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) +
      i.y + vec4(0.0, i1.y, i2.y, 1.0)
    ) +
    i.x + vec4(0.0, i1.x, i2.x, 1.0)
  );
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(
    dot(p0, p0),
    dot(p1, p1),
    dot(p2, p2),
    dot(p3, p3)
  ));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  vec4 m = max(
    0.6 - vec4(
      dot(x0, x0),
      dot(x1, x1),
      dot(x2, x2),
      dot(x3, x3)
    ),
    0.0
  );
  m *= m;
  return 42.0 * dot(
    m * m,
    vec4(
      dot(p0, x0),
      dot(p1, x1),
      dot(p2, x2),
      dot(p3, x3)
    )
  );
}

vec2 textureUvForIndex(float index) {
  return vec2((index + 0.5) / uJointCount, 0.5);
}

vec3 sampleLinearTexture(sampler2D source, float along) {
  float scaledIndex = clamp(along, 0.0, 1.0) * (uJointCount - 1.0);
  float firstIndex = floor(scaledIndex);
  float secondIndex = min(uJointCount - 1.0, firstIndex + 1.0);
  float amount = fract(scaledIndex);
  vec3 firstValue = texture2D(source, textureUvForIndex(firstIndex)).xyz;
  vec3 secondValue = texture2D(source, textureUvForIndex(secondIndex)).xyz;
  return mix(firstValue, secondValue, amount);
}

vec3 sampleChain(float along) {
  return sampleLinearTexture(uChainTexture, along);
}

vec3 sampleFrame(float along) {
  return normalize(sampleLinearTexture(uFrameTexture, along));
}

float endFade(float along) {
  return smoothstep(0.0, 0.1, along) * smoothstep(0.0, 0.1, 1.0 - along);
}

float turbulenceAt(vec3 center, float across, float along) {
  vec3 noisePosition = vec3(
    center.x * uTurbulenceScale + across * 0.48,
    center.y * uTurbulenceScale + uTime * uTurbulenceSpeed,
    center.z * uTurbulenceScale + along * 2.1
  );
  float primary = simplexNoise(noisePosition);
  float secondary = simplexNoise(noisePosition * 1.93 + vec3(7.3, 1.7, 4.1));
  return (primary + secondary * 0.28) * uTurbulenceAmplitude * endFade(along);
}

vec3 deformRibbon(
  vec3 localPosition,
  out vec3 tangent,
  out vec3 widthAxis,
  out vec3 macroNormal
) {
  float along = clamp(localPosition.x / uRibbonLength + 0.5, 0.0, 1.0);
  float jointStep = 1.0 / max(1.0, uJointCount - 1.0);
  float previousAlong = max(0.0, along - jointStep);
  float nextAlong = min(1.0, along + jointStep);
  vec3 center = sampleChain(along);
  vec3 previousCenter = sampleChain(previousAlong);
  vec3 nextCenter = sampleChain(nextAlong);
  tangent = normalize(nextCenter - previousCenter);
  vec3 widthHint = sampleFrame(along);
  widthAxis = normalize(widthHint - tangent * dot(widthHint, tangent));
  vec3 surfaceAxis = normalize(cross(tangent, widthAxis));
  float height = turbulenceAt(center, localPosition.y, along);
  float previousHeight = turbulenceAt(
    previousCenter,
    localPosition.y,
    previousAlong
  );
  float nextHeight = turbulenceAt(
    nextCenter,
    localPosition.y,
    nextAlong
  );
  float widthEpsilon = max(0.025, uRibbonWidth / 80.0);
  float widthHeight = turbulenceAt(
    center,
    localPosition.y + widthEpsilon,
    along
  );
  float alongDistance = max(0.001, length(nextCenter - previousCenter));
  float heightAlong = (nextHeight - previousHeight) / alongDistance;
  float heightAcross = (widthHeight - height) / widthEpsilon;
  macroNormal = normalize(
    surfaceAxis - tangent * heightAlong - widthAxis * heightAcross
  );
  return (
    center +
    widthAxis * localPosition.y +
    surfaceAxis * (localPosition.z + height)
  );
}
`

export const ribbonVertexShader = /* glsl */ `
${deformationFunctions}

uniform vec2 uFabricRepeat;

varying vec2 vFabricUv;
varying vec3 vWorldPosition;
varying vec3 vTangent;
varying vec3 vBaseNormal;
varying float vFacing;

void main() {
  vec3 tangent;
  vec3 widthAxis;
  vec3 macroNormal;
  vec3 deformedPosition = deformRibbon(
    position,
    tangent,
    widthAxis,
    macroNormal
  );
  vec3 deformedNormal = normalize(
    tangent * normal.x +
    widthAxis * normal.y +
    macroNormal * normal.z
  );
  mat3 worldBasis = mat3(modelMatrix);
  vec4 worldPosition = modelMatrix * vec4(deformedPosition, 1.0);
  vWorldPosition = worldPosition.xyz;
  vTangent = normalize(worldBasis * tangent);
  vBaseNormal = normalize(worldBasis * deformedNormal);
  vFabricUv = vec2(
    (position.x / uRibbonLength + 0.5) * uFabricRepeat.x,
    (position.y / uRibbonWidth + 0.5) * uFabricRepeat.y
  );
  vFacing = normal.z;
  gl_Position = projectionMatrix * viewMatrix * worldPosition;
}
`

export const ribbonFragmentShader = /* glsl */ `
uniform sampler2D uFabricNormalMap;
uniform vec3 uBaseColor;
uniform vec3 uLightDirection;
uniform float uAmbient;
uniform float uFabricStrength;
uniform float uSpecularPower;

varying vec2 vFabricUv;
varying vec3 vWorldPosition;
varying vec3 vTangent;
varying vec3 vBaseNormal;
varying float vFacing;

void main() {
  vec3 baseNormal = normalize(vBaseNormal);
  vec3 tangent = normalize(vTangent - baseNormal * dot(vTangent, baseNormal));
  vec3 bitangent = normalize(cross(baseNormal, tangent));
  vec3 textureNormal = texture2D(uFabricNormalMap, vFabricUv).xyz * 2.0 - 1.0;
  textureNormal.xy *= uFabricStrength;
  textureNormal = normalize(textureNormal);
  vec3 normal = normalize(
    tangent * textureNormal.x +
    bitangent * textureNormal.y +
    baseNormal * textureNormal.z
  );
  vec3 lightDirection = normalize(uLightDirection);
  vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
  vec3 halfDirection = normalize(lightDirection + viewDirection);
  float diffuse = max(dot(normal, lightDirection), 0.0);
  float wrappedDiffuse = clamp(
    (dot(normal, lightDirection) + 0.38) / 1.38,
    0.0,
    1.0
  );
  float broadSpecular = pow(
    max(dot(normal, halfDirection), 0.0),
    uSpecularPower
  );
  float transmission = pow(
    max(dot(-normal, lightDirection), 0.0),
    1.6
  );
  float fresnel = pow(
    1.0 - max(dot(normal, viewDirection), 0.0),
    3.0
  );
  float edgeDarkening = mix(0.82, 1.0, abs(vFacing));
  vec3 light =
    vec3(uAmbient) +
    vec3(0.46, 0.43, 0.38) * wrappedDiffuse +
    vec3(0.12, 0.13, 0.14) * diffuse;
  vec3 color = uBaseColor * light * edgeDarkening;
  color += vec3(0.17, 0.16, 0.13) * broadSpecular;
  color += uBaseColor * transmission * 0.075;
  color += vec3(0.06, 0.075, 0.08) * fresnel;
  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`

export const ribbonDepthVertexShader = /* glsl */ `
${deformationFunctions}

void main() {
  vec3 tangent;
  vec3 widthAxis;
  vec3 macroNormal;
  vec3 deformedPosition = deformRibbon(
    position,
    tangent,
    widthAxis,
    macroNormal
  );
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(
    deformedPosition,
    1.0
  );
}
`

export const ribbonDepthFragmentShader = /* glsl */ `
#include <packing>

void main() {
  gl_FragColor = packDepthToRGBA(gl_FragCoord.z);
}
`
